import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import PetsRoundedIcon from '@mui/icons-material/PetsRounded';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient.ts';

type Application = {
  id: string;
  dog_name: string;
  is_crossbreed: boolean;
  breeds: string[];
  crossbreed_unknown: boolean;
  sex: string;
  birth_date: string | null;
  neutered: boolean | null;
  chemically_neutered: boolean | null;
  care_notes: string | null;
  breed_decision: string;
  matched_excluded_breed: string | null;
  status: string;
  created_at: string;
  rejection_reason_key: string | null;
  rejection_subject: string | null;
  rejection_message: string | null;
  rejection_sent_at: string | null;

  families: {
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    phone: string | null;
  } | null;

  phone_call_slots: {
    starts_at: string;
    ends_at: string;
  }[];
};


type IntakeDog = {
  name: string;
  breed: string;
  mix_details: string | null;
  sex: string;
  birth_date: string | null;
  castration_status: string | null;
  female_spayed: boolean | null;
  never_in_heat: boolean;
  last_heat_date: string | null;
  next_heat_month: string | null;
  breed_outcome?: string | null;
};

type IntakeRequest = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  dog_name: string;
  dog_breed: string;
  mix_details: string | null;
  dog_sex: string;
  birth_date: string | null;
  castration_status: string | null;
  female_spayed: boolean | null;
  never_in_heat: boolean;
  last_heat_date: string | null;
  next_heat_month: string | null;
  dogs?: IntakeDog[] | null;
  service_type: string;
  start_date: string;
  end_date: string;
  message: string | null;
  breed_outcome: string;
  status: string;
  invited_user_id: string | null;
  created_at: string;
  admin_contact_status?: string | null;
  admin_contact_note?: string | null;
  admin_contacted_at?: string | null;
  rejection_reason_key?: string | null;
  rejection_subject?: string | null;
  rejection_message?: string | null;
  rejection_sent_at?: string | null;
};

type InformationRequest = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  dog_name: string | null;
  dog_breed: string | null;
  topic: string | null;
  service_type: string | null;
  start_date: string | null;
  end_date: string | null;
  question: string;
  status: string;
  answer_subject: string | null;
  answer_message: string | null;
  answered_at: string | null;
  created_at: string;
};

type CommunicationTarget = {
  kind: "intake" | "dog_application" | "information";
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  dogNames: string[];
  originalQuestion?: string;
};

export const AdminDogApplicationsPage = () => {
  const navigate = useNavigate();

  const [apps, setApps] = useState<Application[]>([]);
  const [intakes, setIntakes] = useState<IntakeRequest[]>([]);
  const [informationRequests, setInformationRequests] =
    useState<InformationRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [approvalApp, setApprovalApp] =
    useState<Application | null>(null);

  const [inviteRequest, setInviteRequest] =
    useState<IntakeRequest | null>(null);

  const [directBooking, setDirectBooking] =
    useState(false);

  const [offLeash, setOffLeash] =
    useState(false);

  const [swimming, setSwimming] =
    useState(false);

  const [privateNotes, setPrivateNotes] =
    useState('');

  const [communicationTarget, setCommunicationTarget] =
    useState<CommunicationTarget | null>(null);
  const [communicationTemplate, setCommunicationTemplate] =
    useState('group_match');
  const [communicationSubject, setCommunicationSubject] =
    useState('');
  const [communicationMessage, setCommunicationMessage] =
    useState('');
  const [communicationBusy, setCommunicationBusy] =
    useState(false);

  const [phoneRequest, setPhoneRequest] =
    useState<IntakeRequest | null>(null);
  const [phoneNote, setPhoneNote] = useState('');

  useEffect(() => {
    load();
  }, []);

  async function load() {
    if (!supabase) return;

    setLoading(true);
    setError('');

    const [
      dogApplicationsResponse,
      intakeResponse,
      informationResponse
    ] = await Promise.all([
        supabase
          .from('dog_applications')
          .select(`
            id,
            dog_name,
            is_crossbreed,
            breeds,
            crossbreed_unknown,
            sex,
            birth_date,
            neutered,
            chemically_neutered,
            care_notes,
            breed_decision,
            matched_excluded_breed,
            status,
            created_at,
            rejection_reason_key,
            rejection_subject,
            rejection_message,
            rejection_sent_at,
            families(
              first_name,
              last_name,
              email,
              phone
            ),
            phone_call_slots(
              starts_at,
              ends_at
            )
          `)
          .order('created_at', {
            ascending: false
          }),

        supabase.rpc(
          'admin_list_intake_requests_v1'
        ),

        supabase.rpc(
          'admin_list_information_requests_v1'
        )
      ]);

    const messages: string[] = [];

    if (dogApplicationsResponse.error) {
      messages.push(
        `Hondenaanmeldingen: ${dogApplicationsResponse.error.message}`
      );
      setApps([]);
    } else {
      setApps(
        (dogApplicationsResponse.data ??
          []) as Application[]
      );
    }

    if (intakeResponse.error) {
      messages.push(
        `Opvangaanvragen: ${intakeResponse.error.message}`
      );
      setIntakes([]);
    } else if (
      !Array.isArray(intakeResponse.data)
    ) {
      messages.push(
        'Opvangaanvragen: de beheerfunctie gaf geen geldige lijst terug.'
      );
      setIntakes([]);
    } else {
      setIntakes(
        intakeResponse.data as IntakeRequest[]
      );
    }

    if (informationResponse.error) {
      messages.push(
        `Informatievragen: ${informationResponse.error.message}`
      );
      setInformationRequests([]);
    } else if (!Array.isArray(informationResponse.data)) {
      messages.push(
        'Informatievragen: de beheerfunctie gaf geen geldige lijst terug.'
      );
      setInformationRequests([]);
    } else {
      setInformationRequests(
        informationResponse.data as InformationRequest[]
      );
    }

    if (messages.length) {
      setError(messages.join(' · '));
    }

    setLoading(false);
  }

  async function copyInviteEmail() {
    if (!inviteRequest) return;

    try {
      await navigator.clipboard.writeText(
        inviteRequest.email
      );
      setSuccess(
        `E-mailadres gekopieerd: ${inviteRequest.email}`
      );
    } catch {
      setError(
        `Kopiëren lukte niet. Gebruik handmatig: ${inviteRequest.email}`
      );
    }
  }

  async function finalizeManualInvite() {
    if (!supabase || !inviteRequest) return;

    const requestId = inviteRequest.id;

    setBusyId(requestId);
    setError('');
    setSuccess('');

    const { data, error } = await supabase.rpc(
      'admin_finalize_intake_after_manual_invite_v1',
      {
        p_request_id: requestId
      }
    );

    if (error) {
      setError(error.message);
      setBusyId(null);
      return;
    }

    const invitedDogCount =
      intakeDogs(inviteRequest).length;

    setInviteRequest(null);
    setSuccess(
      data?.already_finalized
        ? 'Deze Mijn Pippaya-uitnodiging was al gekoppeld.'
        : `De Mijn Pippaya-uitnodiging is gekoppeld. De klant en ${
            invitedDogCount === 1
              ? 'hond staat'
              : `${invitedDogCount} honden staan`
          } nu klaar voor activatie.`
    );

    await load();
    setBusyId(null);
  }

  const rejectionOptions = [
    {
      key: 'group_match',
      label: 'Past niet bij groep / kleinschalige opvang'
    },
    {
      key: 'male_neuter_group',
      label: 'Reu: castratie én groepsgeschiktheid'
    },
    {
      key: 'breed_size_type',
      label: 'Ras / type / formaat past niet'
    },
    {
      key: 'capacity_period',
      label: 'Aangevraagde periode / capaciteit'
    },
    {
      key: 'after_contact',
      label: 'Na telefonisch contact / kennismaking geen match'
    },
    {
      key: 'other',
      label: 'Andere reden / eigen tekst'
    }
  ];

  function rejectionDraft(
    templateKey: string,
    target: CommunicationTarget
  ) {
    const firstName = target.firstName || 'daar';
    const dogText =
      target.dogNames.length > 1
        ? target.dogNames.join(', ')
        : target.dogNames[0] || 'je hond';

    const isExistingDogApplication =
      target.kind === 'dog_application';

    const opening = `Beste ${firstName},

Bedankt voor je interesse in Pippaya Hondenopvang en voor de informatie over ${dogText}.`;

    const closing = `

Ik wens je veel succes met het vinden van een opvangplek die goed bij jullie past.

Groet,

Nicoline
Pippaya Hondenopvang`;

    const finishText = isExistingDogApplication
      ? `Daarom neem ik de aanmelding van ${dogText} niet verder in behandeling.`
      : 'Daarom neem ik deze aanvraag niet verder in behandeling.';

    const declineText = isExistingDogApplication
      ? `Daarom wijs ik de aanmelding van ${dogText} af.`
      : 'Daarom wijs ik de aanvraag af.';

    const reasons: Record<string, string> = {
      group_match:
        `Omdat Pippaya bewust kleinschalig werkt, kijk ik niet alleen naar ras of praktische voorwaarden, maar vooral ook naar het karakter van de hond en de samenstelling van de groep. Op basis van de informatie die ik nu heb, verwacht ik dat ${dogText} niet voldoende passend is binnen mijn huidige manier van opvang en groepssamenstelling. ${finishText}`,
      male_neuter_group:
        `Bij reuen kijk ik niet alleen naar de vraag of een hond gecastreerd of chemisch gecastreerd is. Ook gedrag, spanning, sociale omgang en de combinatie met de andere honden wegen mee. Op basis van de informatie die ik nu heb, kan ik voor ${dogText} geen passende en verantwoorde plek binnen de groep aanbieden. ${declineText}`,
      breed_size_type:
        `Door de kleinschalige opzet van Pippaya en de honden die hier samen verblijven, past niet ieder ras, type of formaat hond binnen de opvang. Voor ${dogText} verwacht ik dat de combinatie met mijn opvang en groepssamenstelling niet passend genoeg is. ${
          isExistingDogApplication
            ? `Daarom kan ik de aanmelding van ${dogText} helaas niet aannemen.`
            : 'Daarom kan ik deze aanvraag helaas niet aannemen.'
        }`,
      capacity_period:
        isExistingDogApplication
          ? `Op dit moment kan ik voor ${dogText} geen passende plek aanbieden binnen de beschikbare capaciteit en groepssamenstelling. Daarom neem ik de aanmelding niet verder in behandeling.`
          : `Voor de aangevraagde periode kan ik op dit moment geen passende plek aanbieden binnen de beschikbare capaciteit en groepssamenstelling. Daarom kan ik deze opvangaanvraag helaas niet aannemen.`,
      after_contact:
        `Naar aanleiding van ons contact en de informatie die we hebben besproken, heb ik de combinatie met mijn kleinschalige opvang en de andere honden opnieuw beoordeeld. Ik verwacht dat dit geen voldoende passende match is voor ${dogText}. ${
          isExistingDogApplication
            ? `Daarom neem ik de aanmelding van ${dogText} niet verder in behandeling.`
            : 'Daarom kan ik de opvang helaas niet aanbieden.'
        }`,
      other:
        isExistingDogApplication
          ? `Na beoordeling van de aanmelding kan ik voor ${dogText} helaas geen passende opvangplek bij Pippaya aanbieden. Daarom neem ik de aanmelding niet verder in behandeling.`
          : `Na beoordeling van de aanvraag kan ik voor ${dogText} helaas geen passende opvangplek bij Pippaya aanbieden.`
    };

    return {
      subject: isExistingDogApplication
        ? `Aanmelding van ${dogText} bij Pippaya Hondenopvang`
        : 'Je opvangaanvraag bij Pippaya Hondenopvang',
      message: `${opening}

${reasons[templateKey] ?? reasons.other}${closing}`
    };
  }

  function openIntakeRejection(request: IntakeRequest) {
    const target: CommunicationTarget = {
      kind: 'intake',
      id: request.id,
      firstName: request.first_name,
      lastName: request.last_name,
      email: request.email,
      dogNames: intakeDogs(request).map(dog => dog.name).filter(Boolean)
    };

    const draft = rejectionDraft('group_match', target);
    setCommunicationTarget(target);
    setCommunicationTemplate('group_match');
    setCommunicationSubject(draft.subject);
    setCommunicationMessage(draft.message);
    setError('');
    setSuccess('');
  }

  function openDogRejection(app: Application) {
    const family = app.families;
    const target: CommunicationTarget = {
      kind: 'dog_application',
      id: app.id,
      firstName: family?.first_name || '',
      lastName: family?.last_name || '',
      email: family?.email || '',
      dogNames: [app.dog_name]
    };

    const initial =
      app.status === 'rejected_breed'
        ? 'breed_size_type'
        : 'group_match';

    const draft = rejectionDraft(initial, target);
    setCommunicationTarget(target);
    setCommunicationTemplate(initial);
    setCommunicationSubject(draft.subject);
    setCommunicationMessage(draft.message);
    setError('');
    setSuccess('');
  }

  function openInformationAnswer(request: InformationRequest) {
    const target: CommunicationTarget = {
      kind: 'information',
      id: request.id,
      firstName: request.first_name,
      lastName: request.last_name,
      email: request.email,
      dogNames: request.dog_name ? [request.dog_name] : [],
      originalQuestion: request.question
    };

    setCommunicationTarget(target);
    setCommunicationTemplate('');
    setCommunicationSubject(
      'Je vraag aan Pippaya Hondenopvang'
    );
    setCommunicationMessage(
      `Beste ${request.first_name},

Bedankt voor je bericht en je interesse in Pippaya Hondenopvang.

[Typ hier je antwoord]

Heb je nog vragen? Laat het gerust weten.

Groet,

Nicoline
Pippaya Hondenopvang`
    );
    setError('');
    setSuccess('');
  }

  function changeCommunicationTemplate(value: string) {
    setCommunicationTemplate(value);

    if (
      communicationTarget &&
      communicationTarget.kind !== 'information'
    ) {
      const draft = rejectionDraft(value, communicationTarget);
      setCommunicationSubject(draft.subject);
      setCommunicationMessage(draft.message);
    }
  }

  async function sendCommunication() {
    if (
      !supabase ||
      !communicationTarget ||
      communicationBusy
    ) {
      return;
    }

    if (
      !communicationSubject.trim() ||
      !communicationMessage.trim()
    ) {
      setError(
        'Onderwerp en e-mailtekst mogen niet leeg zijn.'
      );
      return;
    }

    setCommunicationBusy(true);
    setError('');
    setSuccess('');

    const action =
      communicationTarget.kind === 'intake'
        ? 'reject_intake'
        : communicationTarget.kind === 'dog_application'
          ? 'reject_dog_application'
          : 'answer_information';

    const { data, error: invokeError } =
      await supabase.functions.invoke(
        'pippaya-admin-communication-v1',
        {
          body: {
            action,
            source_id: communicationTarget.id,
            template_key:
              communicationTarget.kind === 'information'
                ? null
                : communicationTemplate,
            subject: communicationSubject.trim(),
            message: communicationMessage.trim()
          }
        }
      );

    if (invokeError) {
      setError(invokeError.message);
      setCommunicationBusy(false);
      return;
    }

    if (data?.error) {
      setError(String(data.error));
      setCommunicationBusy(false);
      return;
    }

    setSuccess(
      data?.message ||
        `E-mail verstuurd naar ${communicationTarget.email}.`
    );
    setCommunicationTarget(null);
    setCommunicationBusy(false);
    await load();
  }

  async function savePhoneContact() {
    if (!supabase || !phoneRequest || busyId) return;

    setBusyId(phoneRequest.id);
    setError('');
    setSuccess('');

    const { error: rpcError } = await supabase.rpc(
      'admin_mark_intake_phone_contact_v1',
      {
        p_request_id: phoneRequest.id,
        p_note: phoneNote.trim() || null
      }
    );

    if (rpcError) {
      setError(rpcError.message);
      setBusyId(null);
      return;
    }

    setSuccess(
      `Telefonisch contact met ${phoneRequest.first_name} is vastgelegd.`
    );
    setPhoneRequest(null);
    setPhoneNote('');
    setBusyId(null);
    await load();
  }

  async function changeStatus(
    id: string,
    status: string
  ) {
    if (!supabase) return;

    setBusyId(id);
    setError('');
    setSuccess('');

    const { data, error } =
      await supabase.rpc(
        'admin_update_dog_application_status',
        {
          p_application_id: id,
          p_new_status: status
        }
      );

    if (error) {
      setError(error.message);
    } else {
      setSuccess(
        data?.[0]?.message ||
          'Status bijgewerkt.'
      );

      await load();
    }

    setBusyId(null);
  }

  function openApproval(
    app: Application
  ) {
    setApprovalApp(app);

    // Veilig standaard alles uit.
    setDirectBooking(false);
    setOffLeash(false);
    setSwimming(false);
    setPrivateNotes('');

    setError('');
    setSuccess('');
  }

  function closeApproval() {
    if (busyId) return;

    setApprovalApp(null);
    setPrivateNotes('');
  }

  async function approveDog() {
    if (!supabase || !approvalApp) {
      return;
    }

    setBusyId(approvalApp.id);
    setError('');
    setSuccess('');

    const { data, error } =
      await supabase.rpc(
        'admin_approve_dog_application',
        {
          p_application_id:
            approvalApp.id,

          p_direct_booking_allowed:
            directBooking,

          p_off_leash_allowed:
            offLeash,

          p_swimming_allowed:
            swimming,

          p_private_notes:
            privateNotes
        }
      );

    if (error) {
      setError(error.message);
      setBusyId(null);
      return;
    }

    setSuccess(
      data?.[0]?.message ||
        'De hond is goedgekeurd voor opvang.'
    );

    setApprovalApp(null);
    setPrivateNotes('');
    setBusyId(null);

    await load();
  }

  const date = (value: string) =>
    new Intl.DateTimeFormat(
      'nl-NL',
      {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      }
    ).format(new Date(value));

  const callDate = (value: string) =>
    new Intl.DateTimeFormat(
      'nl-NL',
      {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit'
      }
    ).format(new Date(value));

  const time = (value: string) =>
    new Intl.DateTimeFormat(
      'nl-NL',
      {
        hour: '2-digit',
        minute: '2-digit'
      }
    ).format(new Date(value));

  const yesNo = (
    value: boolean | null
  ) =>
    value ? 'Ja' : 'Nee';

  const statusText = (
    status: string
  ) => {
    const labels: Record<
      string,
      string
    > = {
      pending_phone_call:
        'Telefonisch kennismaken',

      phone_call_scheduled:
        'Belafspraak gepland',

      needs_review:
        'Beoordeling nodig',

      rejected_breed:
        'Gestopt na rascontrole',

      pending_meeting:
        'Persoonlijke kennismaking',

      approved:
        'Goedgekeurd',

      declined:
        'Afgewezen'
    };

    return labels[status] ?? status;
  };

  const decisionText = (
    decision: string
  ) => {
    const labels: Record<
      string,
      string
    > = {
      continue:
        'Geen automatische blokkade',

      review:
        'Handmatige beoordeling nodig',

      excluded:
        'Automatische rasblokkade'
    };

    return (
      labels[decision] ?? decision
    );
  };


  const intakeStatusText = (
    status: string
  ) => {
    const labels: Record<
      string,
      string
    > = {
      pending: 'Nieuw',
      invited: 'Uitgenodigd',
      rejected: 'Afgewezen'
    };

    return labels[status] ?? status;
  };

  const serviceText = (
    service: string
  ) => {
    const labels: Record<
      string,
      string
    > = {
      dagopvang: 'Dagopvang',
      logeren: 'Logeren',
      feestdagopvang: 'Feestdagopvang'
    };

    return labels[service] ?? service;
  };

  const intakeDogs = (
    request: IntakeRequest
  ): IntakeDog[] => {
    if (
      Array.isArray(request.dogs) &&
      request.dogs.length > 0
    ) {
      return request.dogs;
    }

    return [
      {
        name: request.dog_name,
        breed: request.dog_breed,
        mix_details: request.mix_details,
        sex: request.dog_sex,
        birth_date: request.birth_date,
        castration_status: request.castration_status,
        female_spayed: request.female_spayed,
        never_in_heat: request.never_in_heat,
        last_heat_date: request.last_heat_date,
        next_heat_month: request.next_heat_month,
        breed_outcome: request.breed_outcome
      }
    ];
  };

  const heatText = (
    dog: IntakeDog
  ) => {
    if (dog.sex !== 'teef') {
      return dog.castration_status
        ? `Castratiestatus: ${
            dog.castration_status === 'chemisch'
              ? 'chemisch gecastreerd'
              : dog.castration_status === 'gecastreerd'
                ? 'gecastreerd'
                : 'niet gecastreerd'
          }`
        : 'Castratiestatus niet ingevuld';
    }

    if (dog.female_spayed === true) {
      return 'Gesteriliseerd: ja';
    }

    if (dog.never_in_heat) {
      return 'Niet gesteriliseerd · nog niet loops geweest';
    }

    if (dog.last_heat_date) {
      return `Niet gesteriliseerd · laatste loopsheid ${date(
        dog.last_heat_date
      )}`;
    }

    return 'Niet gesteriliseerd';
  };

  const dogHasHeatWarning = (
    dog: IntakeDog,
    request: IntakeRequest
  ) => {
    if (
      dog.sex !== 'teef' ||
      dog.female_spayed !== false ||
      !dog.next_heat_month
    ) {
      return false;
    }

    const heatStart = new Date(
      `${dog.next_heat_month}T12:00:00`
    );
    const heatEnd = new Date(heatStart);
    heatEnd.setMonth(heatEnd.getMonth() + 1);
    heatEnd.setDate(0);

    const stayStart = new Date(
      `${request.start_date}T12:00:00`
    );
    const stayEnd = new Date(
      `${request.end_date}T12:00:00`
    );

    return (
      stayStart <= heatEnd &&
      stayEnd >= heatStart
    );
  };

  const pendingIntakes = intakes.filter(
    request => request.status === 'pending'
  );

  const pendingInformationRequests =
    informationRequests.filter(
      request => request.status === 'pending'
    );

  if (loading) {
    return (
      <Box
        sx={{
          p: 6,
          textAlign: 'center'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#fffafb',
        py: 4
      }}
    >
      <Container maxWidth="lg">
        <Button
          startIcon={
            <ArrowBackRoundedIcon />
          }
          onClick={() =>
            navigate('/beheer')
          }
        >
          Terug naar beheer
        </Button>

        <Typography
          variant="overline"
          color="text.secondary"
        >
          Pippaya beheer
        </Typography>

        <Typography variant="h3">
          Aanmeldingen & kennismakingen
        </Typography>

        <Typography
          color="text.secondary"
          sx={{ mt: 1 }}
        >
          Beoordeel nieuwe honden en
          beheer de
          kennismakingsprocedure.
        </Typography>

        {error && (
          <Alert
            severity="error"
            sx={{ mt: 3 }}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mt: 3 }}
          >
            {success}
          </Alert>
        )}

        <Card sx={{ p: 2.5, mt: 3 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            gap={1}
            alignItems={{ md: 'center' }}
          >
            <Box>
              <Typography variant="h5">
                Informatievragen
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                Vragen van bezoekers die nog geen prijsberekening of
                opvangaanvraag wilden doen.
              </Typography>
            </Box>

            <Chip
              label={`${pendingInformationRequests.length} open`}
              sx={{
                bgcolor: '#D7E4C0',
                color: '#2E3827',
                fontWeight: 700
              }}
            />
          </Stack>

          <Stack spacing={2} sx={{ mt: 2 }}>
            {pendingInformationRequests.map(request => (
              <Card
                key={request.id}
                variant="outlined"
                sx={{ p: 2.25, boxShadow: 'none' }}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  gap={2}
                >
                  <Box>
                    <Typography variant="h6">
                      {request.first_name} {request.last_name}
                    </Typography>
                    <Typography color="text.secondary">
                      {request.email}
                      {request.phone ? ` · ${request.phone}` : ''}
                    </Typography>
                  </Box>

                  <Chip
                    label="Informatievraag"
                    sx={{
                      bgcolor: '#E9DFF6',
                      color: '#5D437A',
                      fontWeight: 700,
                      alignSelf: 'flex-start'
                    }}
                  />
                </Stack>

                {(request.dog_name ||
                  request.dog_breed ||
                  request.service_type ||
                  request.start_date) && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1.5 }}
                  >
                    {[
                      request.dog_name
                        ? `Hond: ${request.dog_name}`
                        : null,
                      request.dog_breed
                        ? `ras: ${request.dog_breed}`
                        : null,
                      request.service_type
                        ? `opvang: ${serviceText(request.service_type)}`
                        : null,
                      request.start_date
                        ? `vanaf ${date(request.start_date)}`
                        : null,
                      request.end_date &&
                      request.end_date !== request.start_date
                        ? `t/m ${date(request.end_date)}`
                        : null
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>
                )}

                <Alert severity="info" sx={{ mt: 1.5 }}>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap' }}
                  >
                    {request.question}
                  </Typography>
                </Alert>

                <Button
                  variant="contained"
                  sx={{ mt: 1.5 }}
                  onClick={() =>
                    openInformationAnswer(request)
                  }
                >
                  Beantwoorden
                </Button>
              </Card>
            ))}

            {pendingInformationRequests.length === 0 && (
              <Alert severity="info">
                Er zijn momenteel geen open informatievragen.
              </Alert>
            )}
          </Stack>
        </Card>

        <Card sx={{ p: 2.5, mt: 3 }}>
          <Stack
            direction={{
              xs: 'column',
              md: 'row'
            }}
            justifyContent="space-between"
            gap={1}
            alignItems={{ md: 'center' }}
          >
            <Box>
              <Typography variant="h5">
                Nieuwe opvangaanvragen
              </Typography>

              <Typography
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Aanvragen van mensen die nog
                geen Mijn Pippaya-account
                gebruiken.
              </Typography>
            </Box>

            <Chip
              label={`${pendingIntakes.length} open`}
              sx={{
                bgcolor: '#E9DFF6',
                color: '#5D437A',
                fontWeight: 700
              }}
            />
          </Stack>

          <Stack
            spacing={2}
            sx={{ mt: 2 }}
          >
            {pendingIntakes.map(request => {
              const dogs = intakeDogs(request);
              const dogNames = dogs
                .map(dog => dog.name)
                .filter(Boolean)
                .join(', ');

              return (
                <Card
                  key={request.id}
                  variant="outlined"
                  sx={{
                    p: 2.25,
                    boxShadow: 'none'
                  }}
                >
                  <Stack
                    direction={{
                      xs: 'column',
                      md: 'row'
                    }}
                    justifyContent="space-between"
                    gap={2}
                  >
                    <Stack
                      direction="row"
                      spacing={2}
                    >
                      <PetsRoundedIcon />

                      <Box>
                        <Typography variant="h5">
                          {dogNames}
                        </Typography>

                        <Typography
                          color="text.secondary"
                        >
                          {dogs.length === 1
                            ? `${dogs[0].breed}${
                                dogs[0].mix_details
                                  ? ` · ${dogs[0].mix_details}`
                                  : ''
                              }`
                            : `${dogs.length} honden uit hetzelfde gezin`}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {dogs.length > 1 && (
                        <Chip
                          label={`${dogs.length} honden`}
                          sx={{
                            bgcolor: '#E9DFF6',
                            color: '#5D437A',
                            fontWeight: 700,
                            alignSelf: 'flex-start'
                          }}
                        />
                      )}

                      <Chip
                        label={intakeStatusText(
                          request.status
                        )}
                        sx={{
                          bgcolor: '#D7E4C0',
                          color: '#2E3827',
                          fontWeight: 700,
                          alignSelf: 'flex-start'
                        }}
                      />
                    </Stack>
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  <Stack
                    direction={{
                      xs: 'column',
                      md: 'row'
                    }}
                    spacing={5}
                  >
                    <Box flex={1}>
                      <Typography
                        fontWeight={800}
                      >
                        Klant
                      </Typography>

                      <Typography>
                        {request.first_name}{' '}
                        {request.last_name}
                      </Typography>

                      <Typography>
                        {request.email}
                      </Typography>

                      <Typography>
                        {request.phone}
                      </Typography>
                    </Box>

                    <Box flex={1}>
                      <Typography
                        fontWeight={800}
                      >
                        Gewenste opvang
                      </Typography>

                      <Typography>
                        {serviceText(
                          request.service_type
                        )}
                      </Typography>

                      <Typography>
                        {date(request.start_date)}
                        {request.end_date !==
                          request.start_date &&
                          ` t/m ${date(
                            request.end_date
                          )}`}
                      </Typography>

                      <Typography
                        fontWeight={800}
                        sx={{ mt: 2 }}
                      >
                        Rascontrole
                      </Typography>

                      <Typography>
                        {decisionText(
                          request.breed_outcome
                        )}
                      </Typography>

                      <Typography
                        sx={{ mt: 1 }}
                      >
                        Ontvangen:{' '}
                        {date(request.created_at)}
                      </Typography>

                      {request.admin_contact_status === 'phone_contacted' && (
                        <Alert severity="success" sx={{ mt: 1.5 }}>
                          <strong>Telefonisch contact gehad.</strong>
                          {request.admin_contact_note && (
                            <Typography
                              variant="body2"
                              sx={{
                                mt: 0.5,
                                whiteSpace: 'pre-wrap'
                              }}
                            >
                              {request.admin_contact_note}
                            </Typography>
                          )}
                        </Alert>
                      )}
                    </Box>
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  <Typography
                    fontWeight={800}
                    sx={{ mb: 1.25 }}
                  >
                    Hond{dogs.length === 1 ? '' : 'en'}
                  </Typography>

                  <Stack spacing={1.25}>
                    {dogs.map((dog, index) => (
                      <Box
                        key={`${request.id}-${index}-${dog.name}`}
                        sx={{
                          p: 1.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          bgcolor: 'background.paper'
                        }}
                      >
                        <Typography fontWeight={800}>
                          Hond {index + 1} · {dog.name}
                        </Typography>

                        <Typography color="text.secondary">
                          {dog.breed}
                          {dog.mix_details
                            ? ` · ${dog.mix_details}`
                            : ''}
                        </Typography>

                        <Typography>
                          Geslacht: {dog.sex}
                        </Typography>

                        <Typography>
                          Geboortedatum:{' '}
                          {dog.birth_date
                            ? date(dog.birth_date)
                            : 'Niet ingevuld'}
                        </Typography>

                        <Typography>
                          {heatText(dog)}
                        </Typography>

                        {dogHasHeatWarning(
                          dog,
                          request
                        ) && (
                          <Alert
                            severity="warning"
                            sx={{ mt: 1.25 }}
                          >
                            Let op: de verwachte
                            loopsheid van {dog.name}
                            valt in dezelfde maand als
                            de aangevraagde
                            opvangperiode. Loopse teven
                            kunnen niet bij Pippaya
                            worden opgevangen.
                          </Alert>
                        )}
                      </Box>
                    ))}
                  </Stack>

                  {request.message && (
                    <Alert
                      severity="info"
                      sx={{ mt: 2 }}
                    >
                      <strong>
                        Over de hond
                        {dogs.length === 1 ? '' : 'en'}
                      </strong>

                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.5,
                          whiteSpace: 'pre-wrap'
                        }}
                      >
                        {request.message}
                      </Typography>
                    </Alert>
                  )}

                  <Alert
                    severity="info"
                    sx={{ mt: 2 }}
                  >
                    Deze aanvraag is ontvangen
                    via de openbare
                    opvangaanvraag. Beoordeel
                    eerst of de hond
                    {dogs.length === 1 ? '' : 'en'} en
                    periode passen. Daarna kan de
                    persoonlijke Mijn
                    Pippaya-uitnodiging worden
                    verstuurd.
                  </Alert>

                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'row'
                    }}
                    spacing={1}
                    sx={{ mt: 2 }}
                  >
                    <Button
                      variant="contained"
                      disabled={
                        busyId === request.id
                      }
                      onClick={() => {
                        setError('');
                        setSuccess('');
                        setInviteRequest(request);
                      }}
                    >
                      Geschikt → uitnodigen
                    </Button>

                    <Button
                      variant="outlined"
                      disabled={busyId === request.id}
                      onClick={() => {
                        setPhoneRequest(request);
                        setPhoneNote(
                          request.admin_contact_note || ''
                        );
                        setError('');
                        setSuccess('');
                      }}
                    >
                      Telefonisch contact
                    </Button>

                    <Button
                      color="error"
                      variant="outlined"
                      disabled={busyId === request.id}
                      onClick={() =>
                        openIntakeRejection(request)
                      }
                    >
                      Afwijzen
                    </Button>
                  </Stack>
                </Card>
              );
            })}

            {pendingIntakes.length === 0 && (
              <Alert severity="info">
                Er zijn momenteel geen nieuwe
                opvangaanvragen.
              </Alert>
            )}
          </Stack>
        </Card>

        <Typography
          variant="h5"
          sx={{ mt: 4 }}
        >
          Aanmeldingen van bestaande klanten
        </Typography>

        <Typography
          color="text.secondary"
          sx={{ mt: 0.5 }}
        >
          Honden die vanuit Mijn Pippaya zijn
          aangemeld en de
          kennismakingsprocedure doorlopen.
        </Typography>

        <Stack
          spacing={2}
          sx={{ mt: 2 }}
        >
          {apps.map(app => {
            const call =
              app.phone_call_slots?.[0];

            const customer = [
              app.families?.first_name,
              app.families?.last_name
            ]
              .filter(Boolean)
              .join(' ');

            const finished = [
              'approved',
              'declined',
              'rejected_breed'
            ].includes(app.status);

            return (
              <Card
                key={app.id}
                sx={{ p: 2.5 }}
              >
                <Stack
                  direction={{
                    xs: 'column',
                    md: 'row'
                  }}
                  justifyContent="space-between"
                  gap={2}
                >
                  <Stack
                    direction="row"
                    spacing={2}
                  >
                    <PetsRoundedIcon />

                    <Box>
                      <Typography
                        variant="h5"
                      >
                        {app.dog_name}
                      </Typography>

                      <Typography
                        color="text.secondary"
                      >
                        {app.crossbreed_unknown
                          ? 'Kruising · afkomst onbekend'
                          : app.breeds?.join(
                              ' × '
                            ) ||
                            'Ras niet bekend'}
                      </Typography>
                    </Box>
                  </Stack>

                  <Chip
                    label={statusText(
                      app.status
                    )}
                    color={
                      app.status ===
                      'approved'
                        ? 'success'
                        : finished
                          ? 'default'
                          : 'warning'
                    }
                  />
                </Stack>

                <Divider
                  sx={{ my: 2 }}
                />

                <Stack
                  direction={{
                    xs: 'column',
                    md: 'row'
                  }}
                  spacing={5}
                >
                  <Box flex={1}>
                    <Typography
                      fontWeight={800}
                    >
                      Klant
                    </Typography>

                    <Typography>
                      {customer ||
                        'Naam niet ingevuld'}
                    </Typography>

                    <Typography>
                      {app.families
                        ?.email ||
                        'Geen e-mailadres'}
                    </Typography>

                    <Typography>
                      {app.families
                        ?.phone ||
                        'Geen telefoonnummer'}
                    </Typography>

                    <Typography
                      fontWeight={800}
                      sx={{ mt: 2 }}
                    >
                      Hondgegevens
                    </Typography>

                    <Typography>
                      Geslacht: {app.sex}
                    </Typography>

                    <Typography>
                      Geboortedatum:{' '}
                      {app.birth_date
                        ? date(
                            app.birth_date
                          )
                        : 'Niet ingevuld'}
                    </Typography>

                    <Typography>
                      Gecastreerd /
                      gesteriliseerd:{' '}
                      {yesNo(
                        app.neutered
                      )}
                    </Typography>

                    <Typography>
                      Chemisch gecastreerd:{' '}
                      {yesNo(
                        app.chemically_neutered
                      )}
                    </Typography>

                    <Typography
                      sx={{ mt: 1 }}
                    >
                      <strong>
                        Verzorging &
                        bijzonderheden:
                      </strong>

                      <br />

                      {app.care_notes ||
                        'Geen bijzonderheden ingevuld.'}
                    </Typography>
                  </Box>

                  <Box flex={1}>
                    <Typography
                      fontWeight={800}
                    >
                      Interne beoordeling
                    </Typography>

                    <Typography>
                      Rascontrole:{' '}
                      {decisionText(
                        app.breed_decision
                      )}
                    </Typography>

                    {app.matched_excluded_breed && (
                      <Typography>
                        Gevonden
                        uitsluitingsras:{' '}
                        <strong>
                          {
                            app.matched_excluded_breed
                          }
                        </strong>
                      </Typography>
                    )}

                    <Typography>
                      Aanvraag ontvangen:{' '}
                      {date(
                        app.created_at
                      )}
                    </Typography>

                    {call && (
                      <>
                        <Typography
                          fontWeight={800}
                          sx={{ mt: 2 }}
                        >
                          Telefonische
                          kennismaking
                        </Typography>

                        <Typography>
                          {callDate(
                            call.starts_at
                          )}{' '}
                          –{' '}
                          {time(
                            call.ends_at
                          )}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Stack>

                {!finished && (
                  <>
                    <Divider
                      sx={{ my: 2 }}
                    />

                    <Stack spacing={1.5}>
                      {app.status ===
                        'needs_review' && (
                        <Box>
                          <Button
                            variant="contained"
                            disabled={
                              busyId ===
                              app.id
                            }
                            onClick={() =>
                              changeStatus(
                                app.id,
                                'pending_phone_call'
                              )
                            }
                          >
                            Door naar
                            telefonisch
                            kennismaken
                          </Button>
                        </Box>
                      )}

                      {app.status ===
                        'pending_phone_call' && (
                        <Alert severity="info">
                          Wacht op keuze van
                          een belmoment door
                          de klant.
                        </Alert>
                      )}

                      {app.status ===
                        'phone_call_scheduled' && (
                        <Box>
                          <Button
                            variant="contained"
                            disabled={
                              busyId ===
                              app.id
                            }
                            onClick={() =>
                              changeStatus(
                                app.id,
                                'pending_meeting'
                              )
                            }
                          >
                            Telefoongesprek
                            afgerond →
                            persoonlijke
                            kennismaking
                          </Button>
                        </Box>
                      )}

                      {app.status ===
                        'pending_meeting' && (
                        <>
                          <Alert severity="info">
                            Persoonlijke
                            kennismaking
                            uitvoeren. Daarna
                            kun je de
                            kennismaking
                            afronden.
                          </Alert>

                          <Box>
                            <Button
                              variant="contained"
                              disabled={
                                busyId ===
                                app.id
                              }
                              onClick={() =>
                                openApproval(
                                  app
                                )
                              }
                            >
                              Kennismaking
                              afronden
                            </Button>
                          </Box>
                        </>
                      )}

                      <Box>
                        <Button
                          color="error"
                          variant="outlined"
                          disabled={busyId === app.id}
                          onClick={() =>
                            openDogRejection(app)
                          }
                        >
                          Afwijzen
                        </Button>
                      </Box>
                    </Stack>
                  </>
                )}

                {app.status === 'rejected_breed' && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    {app.rejection_sent_at ? (
                      <Alert severity="success">
                        Afwijzingsmail verzonden op{' '}
                        {date(app.rejection_sent_at)}.
                      </Alert>
                    ) : (
                      <Button
                        color="error"
                        variant="outlined"
                        onClick={() =>
                          openDogRejection(app)
                        }
                      >
                        Afwijzingsmail sturen
                      </Button>
                    )}
                  </>
                )}
              </Card>
            );
          })}

          {apps.length === 0 && (
            <Alert severity="info">
              Er zijn momenteel geen
              hondenaanmeldingen van bestaande
              klanten.
            </Alert>
          )}
        </Stack>
      </Container>

      <Dialog
        open={!!phoneRequest}
        onClose={() => {
          if (!busyId) {
            setPhoneRequest(null);
            setPhoneNote('');
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Telefonisch contact</DialogTitle>
        <DialogContent>
          {phoneRequest && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="info">
                Leg hier kort vast wat telefonisch is besproken.
                De opvangaanvraag blijft open; daarna kun je nog
                uitnodigen of afwijzen.
              </Alert>

              <Typography>
                <strong>
                  {phoneRequest.first_name}{' '}
                  {phoneRequest.last_name}
                </strong>
                {' · '}
                {intakeDogs(phoneRequest)
                  .map(dog => dog.name)
                  .join(', ')}
              </Typography>

              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Notitie telefonisch contact"
                value={phoneNote}
                onChange={event =>
                  setPhoneNote(event.target.value)
                }
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={!!busyId}
            onClick={() => {
              setPhoneRequest(null);
              setPhoneNote('');
            }}
          >
            Annuleren
          </Button>
          <Button
            variant="contained"
            disabled={
              !phoneRequest ||
              busyId === phoneRequest.id
            }
            onClick={() => void savePhoneContact()}
          >
            Opslaan
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!communicationTarget}
        onClose={() => {
          if (!communicationBusy) {
            setCommunicationTarget(null);
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {communicationTarget?.kind === 'information'
            ? 'Informatievraag beantwoorden'
            : 'Aanvraag afwijzen en e-mail versturen'}
        </DialogTitle>

        <DialogContent>
          {communicationTarget && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert
                severity={
                  communicationTarget.kind === 'information'
                    ? 'info'
                    : 'warning'
                }
              >
                {communicationTarget.kind === 'information'
                  ? 'Controleer en pas je antwoord aan voordat je het verstuurt.'
                  : 'De aanvraag wordt pas als afgewezen gemarkeerd nadat de e-mail succesvol is verstuurd.'}
              </Alert>

              <Box>
                <Typography fontWeight={800}>Aan</Typography>
                <Typography>
                  {communicationTarget.firstName}{' '}
                  {communicationTarget.lastName}
                </Typography>
                <Typography color="text.secondary">
                  {communicationTarget.email}
                </Typography>
              </Box>

              {communicationTarget.kind !== 'information' && (
                <FormControl fullWidth>
                  <InputLabel>Standaardreden</InputLabel>
                  <Select
                    label="Standaardreden"
                    value={communicationTemplate}
                    onChange={event =>
                      changeCommunicationTemplate(
                        String(event.target.value)
                      )
                    }
                  >
                    {rejectionOptions.map(option => (
                      <MenuItem
                        key={option.key}
                        value={option.key}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <TextField
                fullWidth
                label="Onderwerp"
                value={communicationSubject}
                onChange={event =>
                  setCommunicationSubject(
                    event.target.value
                  )
                }
              />

              <TextField
                fullWidth
                multiline
                minRows={12}
                label={
                  communicationTarget.kind === 'information'
                    ? 'Antwoord'
                    : 'E-mailtekst'
                }
                value={communicationMessage}
                onChange={event =>
                  setCommunicationMessage(
                    event.target.value
                  )
                }
              />
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            disabled={communicationBusy}
            onClick={() =>
              setCommunicationTarget(null)
            }
          >
            Annuleren
          </Button>

          <Button
            variant="contained"
            color={
              communicationTarget?.kind === 'information'
                ? 'primary'
                : 'error'
            }
            disabled={
              communicationBusy ||
              !communicationTarget ||
              !communicationSubject.trim() ||
              !communicationMessage.trim()
            }
            onClick={() =>
              void sendCommunication()
            }
          >
            {communicationBusy
              ? 'Versturen...'
              : communicationTarget?.kind === 'information'
                ? 'Antwoord versturen'
                : 'Afwijzen & e-mail versturen'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!inviteRequest}
        onClose={() => {
          if (!busyId) {
            setInviteRequest(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Mijn Pippaya-uitnodiging
        </DialogTitle>

        <DialogContent>
          {inviteRequest && (
            <Stack
              spacing={2}
              sx={{ mt: 1 }}
            >
              <Alert severity="info">
                Gebruik deze route alleen als je{' '}
                <strong>
                  {intakeDogs(inviteRequest)
                    .map(dog => dog.name)
                    .join(', ')}
                </strong>{' '}
                geschikt vindt om verder te gaan.
              </Alert>

              <Box>
                <Typography fontWeight={800}>
                  Klant
                </Typography>
                <Typography>
                  {inviteRequest.first_name}{' '}
                  {inviteRequest.last_name}
                </Typography>
                <Typography
                  sx={{ overflowWrap: 'anywhere' }}
                >
                  {inviteRequest.email}
                </Typography>
              </Box>

              <Alert severity="warning">
                De uitnodiging zelf wordt veilig via
                Supabase Authentication verstuurd.
                De website bevat bewust geen
                service-role-sleutel.
              </Alert>

              <Box>
                <Typography fontWeight={800}>
                  Stap 1
                </Typography>
                <Typography>
                  Open Supabase → Authentication →
                  Users → Invite user en nodig exact
                  dit e-mailadres uit.
                </Typography>

                <Stack
                  direction={{
                    xs: 'column',
                    sm: 'row'
                  }}
                  spacing={1}
                  sx={{ mt: 1.5 }}
                >
                  <Button
                    variant="outlined"
                    onClick={() =>
                      void copyInviteEmail()
                    }
                  >
                    E-mailadres kopiëren
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={() =>
                      window.open(
                        'https://supabase.com/dashboard/project/elmhcpenxbpuchbvwdbf/auth/users',
                        '_blank',
                        'noopener,noreferrer'
                      )
                    }
                  >
                    Supabase Users openen
                  </Button>
                </Stack>
              </Box>

              <Box>
                <Typography fontWeight={800}>
                  Stap 2
                </Typography>
                <Typography>
                  Zodra Supabase meldt dat de
                  uitnodiging is verstuurd, kom je
                  terug en klik je hieronder op
                  “Uitnodiging is verstuurd –
                  koppelen”.
                </Typography>
              </Box>

              <Alert severity="info">
                Bij het koppelen maakt Pippaya het
                klantprofiel en de hondenprofielen
                aan en koppelt deze veilig aan het
                nieuwe Mijn Pippaya-account. De
                honden kunnen daarna nog niet
                automatisch direct boeken; dat
                beheer je later bij Honden.
              </Alert>
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            disabled={!!busyId}
            onClick={() =>
              setInviteRequest(null)
            }
          >
            Sluiten
          </Button>

          <Button
            variant="contained"
            disabled={
              !inviteRequest ||
              busyId === inviteRequest.id
            }
            onClick={() =>
              void finalizeManualInvite()
            }
          >
            {busyId === inviteRequest?.id
              ? 'Koppelen...'
              : 'Uitnodiging is verstuurd – koppelen'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!approvalApp}
        onClose={closeApproval}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Kennismaking afronden
        </DialogTitle>

        <DialogContent>
          {approvalApp && (
            <Stack
              spacing={2}
              sx={{ mt: 1 }}
            >
              <Alert severity="info">
                Je staat op het punt{' '}
                <strong>
                  {approvalApp.dog_name}
                </strong>{' '}
                goed te keuren voor
                Pippaya Hondenopvang.
              </Alert>

              <Typography>
                Kies hieronder bewust
                welke toestemmingen voor
                deze hond gelden. Alles
                staat standaard uit.
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      directBooking
                    }
                    onChange={e =>
                      setDirectBooking(
                        e.target.checked
                      )
                    }
                  />
                }
                label="Direct boeken toegestaan"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={offLeash}
                    onChange={e =>
                      setOffLeash(
                        e.target.checked
                      )
                    }
                  />
                }
                label="Loslopen toegestaan"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={swimming}
                    onChange={e =>
                      setSwimming(
                        e.target.checked
                      )
                    }
                  />
                }
                label="Zwemmen toegestaan"
              />

              <TextField
                label="Interne notities"
                value={privateNotes}
                onChange={e =>
                  setPrivateNotes(
                    e.target.value
                  )
                }
                multiline
                minRows={4}
                helperText="Alleen zichtbaar voor Pippaya-beheer."
              />

              <Alert severity="warning">
                Na goedkeuring wordt deze
                hond als actieve hond aan
                het klantaccount
                toegevoegd.
              </Alert>
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            disabled={!!busyId}
            onClick={closeApproval}
          >
            Annuleren
          </Button>

          <Button
            variant="contained"
            disabled={
              !approvalApp ||
              !!busyId
            }
            onClick={approveDog}
          >
            {busyId
              ? 'Goedkeuren...'
              : 'Goedkeuren voor opvang'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};